import React from 'react';
import LessonChords from './LessonChords';
import LessonInfo from './LessonInfo';
import LessonTablature from './LessonTablature';
import LessonTone from './LessonTone';
import ScrollContainer from '../../../components/ScrollContainer';
import CurriculumTable from '../../../components/CurriculumTable';
import Tuner from '../../../components/Tuner';
import { formatTableData } from '../../../utils/browse-utils';
import segment from '../../../core/segment';

export const canReposition = windowWidth => windowWidth >= 960;

export const getNextLesson = (currentLessonSlug, pathsWatching, coursesWatching) => {
  const { nextLesson: pathNext } = pathsWatching || {};
  const { nextLesson: courseNext, currentCourse } = coursesWatching || {};
  const { lessons } = currentCourse || {};
  const nextLesson = pathNext || courseNext || {};
  const { slug } = nextLesson;
  if (slug === currentLessonSlug && lessons && lessons.length) {
    const [nextLessonInCourse] = lessons;
    return nextLessonInCourse;
  }
  return nextLesson;
};

export const getContentOffset = () => {
  const { innerWidth } = window || {};
  return (innerWidth * 9) / 16;
};

export const getVideoTrackData = (lesson, userID) => {
  if (!lesson) return {};
  const { id } = lesson;
  const lessonVideo = lesson['activity-lesson-video'] || {};
  const { id: videoID } = lessonVideo || {};
  return {
    fc_id: userID,
    lesson_id: id,
    video_id: videoID,
    video_player: 'lesson flowplayer',
  };
};

export const findLessonInFlatLessons = (lesson, flatLessons) => {
  if (!lesson || !flatLessons || !flatLessons.length) return null;
  const { id } = lesson;
  return flatLessons.find(flatLesson => flatLesson.id === id);
};

export const getLessonPropswithSlug = (lessonSlug, paths, courses, lessons) => {
  const curriculum = [paths, courses, lessons];
  const lessonProps =
    curriculum.find(item => {
      const { currentLesson } = item || {};
      const { slug = '' } = currentLesson || {};
      return slug === lessonSlug;
    }) || {};
  return lessonProps;
};

const getNextLessonType = lesson => {
  const { 'activity-type': activityType, slug } = lesson;
  if (activityType === 'practice-sheet') return `/practice/${slug}`;
  return `/lesson/${slug}`;
};

export const getNextUrl = (currentUrl, lessonProps) => {
  const { currentLesson, nextLesson, nextCourse } = lessonProps;
  const { 'activity-type': currentActivityType } = currentLesson || {};
  const isLesson = currentActivityType === 'lesson';
  const type = isLesson ? '/lesson/' : '/practice';
  let URL = currentLesson ? currentUrl.slice(0, currentUrl.indexOf(type)) : '/';
  if (nextLesson) {
    URL += getNextLessonType(nextLesson);
  } else if (nextCourse) {
    const { slug: courseSlug, lessons } = nextCourse;
    URL = URL.slice(0, URL.indexOf('/course/'));
    URL += `/course/${courseSlug}${getNextLessonType(lessons[0])}`;
  }
  return URL;
};

export const preventRightClick = event => {
  if (event.type === 'contextmenu') {
    event.preventDefault();
  }
};

export const getLessonProgressFromMyProgress = (lessonId, myProgress) => {
  if (!lessonId || !myProgress) return null;
  const { lessons } = myProgress;
  return lessons[lessonId] || null;
};

const getNavItemInfo = (lesson, appleLink) => ({
  button: { title: 'Info', icon: 'info-circle-fill' },
  content: <LessonInfo appleLink={appleLink} lesson={lesson} />,
});

const getNavItemChords = chords => ({
  button: { title: 'Chords', icon: 'chord-diagram' },
  content: <LessonChords chords={chords} />,
});

const getNavItemTone = (course, lesson, presets) => ({
  button: { title: 'Tone', icon: 'amp' },
  content: <LessonTone course={course} lesson={lesson} presets={presets} />,
});

const getNavItemTabs = (instrument, level, tabs, type, toggleFullScreen) => ({
  button: { title: 'Tabs', icon: 'tablature' },
  content: (
    <LessonTablature
      instrument={instrument}
      level={level}
      tabs={tabs}
      type={type}
      toggleFullScreen={toggleFullScreen}
    />
  ),
});

const getNavItemRelated = relatedItems => {
  const { results } = relatedItems || {};
  const { items } = results || {};
  const table = items ? (
    <CurriculumTable
      content={formatTableData('lesson', null, items, true)}
      display={{ chords: false, level: false, minutes: true, type: true }}
      small
    />
  ) : null;
  return {
    button: { title: 'Related', icon: 'browse' },
    content: table,
  };
};

export const getNavItems = (lesson, relatedItems, course, flags, toggleFullScreen) => {
  if (!lesson) return [];
  const { instrument, level, tabs, type } = lesson || {};
  const lessonVideo = lesson['activity-lesson-video'] || {};
  const { chords } = lessonVideo;
  const appleLink = course ? course['apple-affiliate-link'] : null;
  const navArr = [getNavItemInfo(lesson, appleLink)];
  const { results } = relatedItems || {};
  const { items } = results || {};
  const presets = lesson['tone-presets'];
  if (chords) {
    navArr.push(getNavItemChords(chords));
  }
  if (tabs) {
    navArr.push(getNavItemTabs(instrument, parseInt(level, 10), tabs, type, toggleFullScreen));
  }
  if (presets) {
    navArr.push(getNavItemTone(course, lesson, presets));
  }
  navArr.push({
    button: { title: 'Feedback', icon: 'chat' },
    content: null,
  });
  if (items) {
    navArr.push(getNavItemRelated(relatedItems));
  }
  navArr.push({
    button: { title: 'Tools', icon: 'tuning-fork' },
    content: <Tuner />,
  });
  return navArr;
};

export const hasCurriculumChanged = (prev, next) => {
  const { slug: prevSlug } = prev || {};
  const { slug: nextSlug } = next || {};
  return prevSlug !== nextSlug;
};

export const isSongRiff = type => type === 'song' || type === 'riff';

export const tabModalEventTrack = (lessonName, modalOpening) => {
  const tabState = modalOpening ? 'Expanded' : 'Collapsed';
  const properties = { lessonName };
  segment.event(`Tablature ${tabState}`, properties);
};

export const isDesktop = () => window.innerWidth > 1280;
export const isTabletLandscape = () => window.innerWidth > 960;

export const isLandscape = () => !isDesktop() && window.innerWidth > window.innerHeight;

export const connectScrollHandler = handleTargetY => {
  return wrappedComponent => {
    return <ScrollContainer onTargetY={handleTargetY}>{wrappedComponent}</ScrollContainer>;
  };
};

export const preventBodyScrollIfModalOpening = isModalOpening => {
  if (isModalOpening) {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
  } else {
    document.getElementsByTagName('body')[0].style.overflow = null;
  }
};
